import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X, WhatsappLogo, ArrowUpRight } from "@phosphor-icons/react";
import { BRAND, SERVICE_CATEGORIES } from "@/data/content";
import { SERVICE_PAGES } from "@/data/servicePages";

export function Logo({ className = "" }) {
    return (
        <Link to="/" data-testid="brand-logo" className={`group flex items-baseline gap-1.5 ${className}`}>
            <span className="font-display text-xl font-black tracking-[0.18em] text-white">HIGHTS</span>
            <span className="h-1.5 w-1.5 bg-white transition-transform duration-300 group-hover:scale-150" />
        </Link>
    );
}

const NAV_LINKS = [
    { label: "The System", href: "/#system" },
    { label: "Digital Team", href: "/#team" },
    { label: "Demos", href: "/#demos" },
    { label: "Simulator", href: "/#simulator" },
    { label: "Services", href: "/#services" },
];

export function Nav() {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const go = (href) => {
        setOpen(false);
        if (href.startsWith("/#")) {
            const id = href.slice(2);
            if (window.location.pathname !== "/") {
                navigate("/");
                setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }
        } else {
            navigate(href);
        }
    };
    return (
        <>
            <header className="fixed inset-x-0 top-0 z-[90] border-b border-white/10 bg-black/60 backdrop-blur-2xl" data-testid="main-nav">
                <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:px-10">
                    <Logo />
                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.label}
                                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                                onClick={() => go(l.href)}
                                className="text-xs font-semibold uppercase tracking-[0.18em] text-white/55 transition-colors duration-300 hover:text-white"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-3">
                        <Link
                            to="/book"
                            data-testid="nav-book-button"
                            className="hidden items-center gap-2 border border-white/25 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-black sm:flex"
                        >
                            Book consultation
                        </Link>
                        <button
                            data-testid="nav-menu-button"
                            onClick={() => setOpen(true)}
                            className="flex h-10 w-10 items-center justify-center border border-white/20 text-white lg:hidden"
                            aria-label="Open menu"
                        >
                            <List size={20} />
                        </button>
                    </div>
                </div>
            </header>
            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex flex-col bg-black/95 backdrop-blur-xl"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-16 items-center justify-between px-5">
                            <Logo />
                            <button data-testid="nav-menu-close" onClick={() => setOpen(false)} className="flex h-10 w-10 items-center justify-center border border-white/20 text-white" aria-label="Close menu">
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
                            {[...NAV_LINKS, { label: "Book consultation", href: "/book" }].map((l, i) => (
                                <motion.button
                                    key={l.label}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i }}
                                    onClick={() => go(l.href)}
                                    className="border-b border-white/10 py-5 text-left font-display text-3xl font-bold tracking-tight text-white/80 transition-colors hover:text-white"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

export function WhatsAppButton() {
    const href = `https://wa.me/${BRAND.phoneIntl}?text=${encodeURIComponent(BRAND.whatsappMsg)}`;
    return (
        <motion.a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-button"
            aria-label="Chat with HIGHTS on WhatsApp"
            className="group fixed bottom-5 right-5 z-[85] flex items-center gap-0 rounded-full border border-white/20 bg-white text-black shadow-[0_8px_40px_rgba(0,0,0,0.6)] transition-all duration-300 hover:gap-2.5 px-4 py-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.5 }}
        >
            <WhatsappLogo size={22} weight="fill" />
            <span className="max-w-0 overflow-hidden whitespace-nowrap text-xs font-bold uppercase tracking-widest transition-all duration-300 group-hover:max-w-[120px]">
                WhatsApp us
            </span>
        </motion.a>
    );
}

export function StickyMobileCTA() {
    const [show, setShow] = useState(false);
    useEffect(() => {
        const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.9);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-x-0 bottom-0 z-[80] border-t border-white/10 bg-black/80 p-3 backdrop-blur-xl sm:hidden"
                    initial={{ y: 80 }}
                    animate={{ y: 0 }}
                    exit={{ y: 80 }}
                    data-testid="sticky-mobile-cta"
                >
                    <Link to="/book" className="flex w-full items-center justify-center gap-2 bg-white py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-black">
                        Book a free consultation <ArrowUpRight size={16} />
                    </Link>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export function Footer() {
    const serviceLinks = Object.keys(SERVICE_PAGES).slice(0, 8);
    return (
        <footer className="border-t border-white/10 bg-void" data-testid="site-footer">
            <div className="mx-auto max-w-[1400px] px-5 py-20 md:px-10">
                <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <span className="font-display text-4xl font-black tracking-[0.12em] text-white">HIGHTS</span>
                        <p className="mt-4 max-w-sm text-sm font-light leading-relaxed text-white/50">
                            {BRAND.tagline} Websites, AI employees, CRM, automation and optimisation — one connected growth system for businesses of any size.
                        </p>
                        <div className="mt-8 space-y-2 text-sm text-white/60">
                            <p><a className="transition-colors hover:text-white" href={`mailto:${BRAND.email}`} data-testid="footer-email">{BRAND.email}</a></p>
                            <p><a className="transition-colors hover:text-white" href={`tel:${BRAND.phoneDisplay.replace(/\s/g, "")}`} data-testid="footer-phone">{BRAND.phoneDisplay}</a></p>
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Services</p>
                        <ul className="mt-5 space-y-3">
                            {serviceLinks.map((slug) => (
                                <li key={slug}>
                                    <Link to={`/services/${slug}`} className="text-sm font-light text-white/60 transition-colors hover:text-white" data-testid={`footer-service-${slug}`}>
                                        {SERVICE_PAGES[slug].h1.split("—")[0].split(",")[0]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-2">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">Explore</p>
                        <ul className="mt-5 space-y-3 text-sm font-light text-white/60">
                            <li><Link className="transition-colors hover:text-white" to="/demos/property">Estate agency demo</Link></li>
                            <li><Link className="transition-colors hover:text-white" to="/demos/golf">Golf & hospitality demo</Link></li>
                            <li><Link className="transition-colors hover:text-white" to="/demos/app/fitness">Fitness app demo</Link></li>
                            <li><Link className="transition-colors hover:text-white" to="/demos/app/market">Marketplace app demo</Link></li>
                            <li><Link className="transition-colors hover:text-white" to="/book">Book a consultation</Link></li>
                        </ul>
                    </div>
                    <div className="md:col-span-2">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/40">System</p>
                        <ul className="mt-5 space-y-3 text-sm font-light text-white/60">
                            {SERVICE_CATEGORIES.map((c) => (
                                <li key={c.id}><span className="uppercase tracking-widest">{c.label}</span></li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-white/35 md:flex-row md:items-center md:justify-between">
                    <p>© {new Date().getFullYear()} HIGHTS. All rights reserved.</p>
                    <p className="font-mono tracking-widest">BUILD — AUTOMATE — OPTIMISE — GROW</p>
                </div>
            </div>
        </footer>
    );
}
